/**
 * Detection-category → Hugeicons glyph map.
 *
 * The report data carries a category *key* (`'knife'`, `'trademark'`, …) rather than a component,
 * because that is what an API returns. This module is the single place that turns a key into a
 * glyph, so adding a category is a one-line change and the fixture stays free of JSX imports.
 *
 * Glyphs come from `@hugeicons-pro/core-stroke-standard` and are rendered with `HugeiconsIcon`
 * (see the `hugeicons-icons` skill). Sizing lives in `static/brandSafety.scss`.
 */

import {
  BottleWineIcon,
  CigaretteIcon,
  FlaskConicalIcon,
  GunIcon,
  Image01Icon,
  Knife01Icon,
  TradeMarkIcon,
} from '@hugeicons-pro/core-stroke-standard';

export const BRAND_SAFETY_ICON_KEYS = {
  KNIFE: 'knife',
  GUN: 'gun',
  ALCOHOL: 'alcohol',
  TOBACCO: 'tobacco',
  SUBSTANCE: 'substance',
  TRADEMARK: 'trademark',
};

const ICONS = {
  [BRAND_SAFETY_ICON_KEYS.KNIFE]: Knife01Icon,
  [BRAND_SAFETY_ICON_KEYS.GUN]: GunIcon,
  [BRAND_SAFETY_ICON_KEYS.ALCOHOL]: BottleWineIcon,
  [BRAND_SAFETY_ICON_KEYS.TOBACCO]: CigaretteIcon,
  [BRAND_SAFETY_ICON_KEYS.SUBSTANCE]: FlaskConicalIcon,
  [BRAND_SAFETY_ICON_KEYS.TRADEMARK]: TradeMarkIcon,
};

/** Resolve a category key to a glyph, falling back to a generic image icon. */
export function resolveDetectionIcon(key) {
  return ICONS[key] ?? Image01Icon;
}

/**
 * API detection categories (parsed from flag names — `Alcohol`, `Trademark`, `Smoking`, …) → the
 * icon key above. Categories with no dedicated glyph (Hate/Politics/Religion/Adult) fall through to
 * the generic image icon via `resolveDetectionIcon`.
 */
export const CATEGORY_ICON_KEY = {
  Alcohol: BRAND_SAFETY_ICON_KEYS.ALCOHOL,
  Trademark: BRAND_SAFETY_ICON_KEYS.TRADEMARK,
  Smoking: BRAND_SAFETY_ICON_KEYS.TOBACCO,
  Marijuana: BRAND_SAFETY_ICON_KEYS.SUBSTANCE,
  Narcotics: BRAND_SAFETY_ICON_KEYS.SUBSTANCE,
  Therapeutics: BRAND_SAFETY_ICON_KEYS.SUBSTANCE,
  Knives: BRAND_SAFETY_ICON_KEYS.KNIFE,
  Guns: BRAND_SAFETY_ICON_KEYS.GUN,
};

/** Category display name → icon key (undefined → generic, handled by resolveDetectionIcon). */
export function iconKeyForCategory(category) {
  return CATEGORY_ICON_KEY[category];
}
